import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Box, makeStyles, Theme, Typography } from '@material-ui/core'
import clsx from 'clsx'
import React, {
    FC,
    KeyboardEvent,
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useRef,
} from 'react'

import listingColors from 'pages/ProductsPage/ProductDetailsPage/listingColors'
import { trapTabKey } from 'shared/utils/trapFocus'

import { ERROR_DUPLICATE_OPTION, ERROR_EMPTY_FIELD } from '../constants'

const { premiumPrimary, premiumSecondary, errorColor } = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    uomContainer: {
        display: 'inline-flex',
        alignItems: 'baseline',
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: `${premiumPrimary}33`,
        border: `1px solid ${premiumPrimary}`,
        '& > input': {
            marginLeft: theme.spacing(0.5),
            marginRight: theme.spacing(0.5),
            border: `1px solid ${premiumPrimary}`,
            borderRadius: theme.spacing(0.5),
            '&:focus': {
                borderColor: premiumSecondary,
                outline: 'none',
            },
        },
    },
    uomInput: {
        boxSizing: 'border-box',
        fontSize: pxToRem(13),
        height: pxToRem(23),
        width: '12ch',
        maxWidth: '30ch',
        textOverflow: 'ellipsis',
        marginRight: theme.spacing(1),
        border: `1px solid ${premiumPrimary}`,
        '&:hover': {
            cursor: 'pointer',
        },
        '&:focus': {
            outline: `1px solid ${premiumPrimary}`,
        },
        '&.error': {
            border: `1px solid ${errorColor}`,
            outline: 'none',
        },
    },
    metric: { marginRight: 0 },
    uomInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        margin: theme.spacing(0.25),
    },
    errorLabel: {
        color: errorColor,
        height: pxToRem(8),
        fontSize: pxToRem(10),
    },
}))

interface UOMInterface {
    error: Record<string, string>
    setError: (error: Record<string, string> | string) => void
    handleInsertPremiumOption?: (property: Record<string, string>) => boolean
    setIsUOMActive?: (isUOMActive: boolean) => void
    imperialValue?: string
    metricValue?: string
    onCancel?: () => void
}

const UnitsOfMeasurement: FC<UOMInterface> = ({
    error,
    setError,
    handleInsertPremiumOption,
    setIsUOMActive,
    imperialValue = '',
    metricValue = '',
    onCancel,
}) => {
    const classes = useStyles()

    const uomContainerRef: RefObject<HTMLDivElement> = useRef(null)
    const imperialRef: RefObject<HTMLInputElement> = useRef(null)
    const metricRef: RefObject<HTMLInputElement> = useRef(null)
    const keyCombiInitRef: MutableRefObject<boolean> = useRef(false)

    const checkRequired = useCallback(() => {
        if (imperialRef.current && !imperialRef.current.value) {
            setError({ imperial: ERROR_EMPTY_FIELD })
            return false
        } else if (metricRef.current && !metricRef.current.value) {
            setError({ metric: ERROR_EMPTY_FIELD })
            return false
        }

        return true
    }, [setError])

    const insertUOM = useCallback(() => {
        if (handleInsertPremiumOption) {
            const hasError = handleInsertPremiumOption({
                metricUnits: metricRef.current?.value ?? '',
                imperialUnits: imperialRef.current?.value ?? '',
            })

            if (hasError) {
                setError({
                    imperial: ERROR_DUPLICATE_OPTION,
                    metric: ERROR_DUPLICATE_OPTION,
                })
                return
            }

            setIsUOMActive?.(false)
        }
    }, [handleInsertPremiumOption])

    const handleKeydown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Escape') {
                onCancel?.()
            }

            if (error) {
                setError('')
            }

            if (e.ctrlKey && e.key === '<') {
                if (!keyCombiInitRef.current) {
                    keyCombiInitRef.current = true
                    return
                }
                setIsUOMActive?.(false)
            }

            if (metricRef.current && metricRef.current.value.length > 11) {
                metricRef.current.style.width = `${metricRef.current.value.length}ch`
            }
            if (imperialRef.current && imperialRef.current.value.length > 11) {
                imperialRef.current.style.width = `${imperialRef.current.value.length}ch`
            }

            if (e.key === 'Enter') {
                if (!checkRequired()) {
                    return
                }

                insertUOM()
            }
        },
        [error, handleInsertPremiumOption, setIsUOMActive, onCancel]
    )

    const clickedOutside = useCallback(
        (e) => {
            if (
                uomContainerRef.current &&
                !uomContainerRef.current?.contains(e.target)
            ) {
                if (!checkRequired()) {
                    return
                }

                insertUOM()
            }
        },
        [handleInsertPremiumOption]
    )

    useEffect(() => {
        if (uomContainerRef.current) {
            uomContainerRef.current?.addEventListener(
                'keydown',
                trapTabKey(uomContainerRef)
            )
            window.addEventListener('click', clickedOutside)

            imperialRef.current?.focus()
            return () => {
                uomContainerRef.current?.removeEventListener(
                    'keydown',
                    trapTabKey
                )
                window.removeEventListener('click', clickedOutside)
            }
        }
    }, [])

    return (
        <GridItem ref={uomContainerRef} className={classes.uomContainer}>
            <Box className={classes.uomInputContainer}>
                <input
                    className={clsx(classes.uomInput, {
                        error: !!error.imperial,
                    })}
                    ref={imperialRef}
                    name="im"
                    placeholder="imperial"
                    type="text"
                    onKeyDown={handleKeydown}
                    maxLength={150}
                    required
                    defaultValue={imperialValue}
                ></input>
                {!!error && !!error.imperial && (
                    <Typography component="div" className={classes.errorLabel}>
                        {error.imperial}
                    </Typography>
                )}
            </Box>
            (
            <Box className={classes.uomInputContainer}>
                <input
                    className={clsx(classes.uomInput, classes.metric, {
                        error: !!error.metric,
                    })}
                    ref={metricRef}
                    name="si"
                    placeholder="metric"
                    type="text"
                    onKeyDown={handleKeydown}
                    maxLength={150}
                    required
                    defaultValue={metricValue}
                ></input>
                {!!error && !!error.metric && (
                    <Typography component="div" className={classes.errorLabel}>
                        {error.metric}
                    </Typography>
                )}
            </Box>
            )
        </GridItem>
    )
}

export default UnitsOfMeasurement
